<template>
  <!-- Surgeon Secretary Dashboard -->
  <div
    class="dashboard-page"
    data-test-id="surgeon-secretary-dashboard"
  >
    <VCard class="mb-4">
      <VCardText>
        <div class="d-flex align-center justify-space-between">
          <div>
            <h2 class="text-h4 mb-2">
              Welcome, {{ userName }}
            </h2>
            <p class="text-body-2 text-medium-emphasis">
              Manage listings and applications from your dashboard.
            </p>
          </div>
          <VIcon
            size="x-large"
            color="primary"
          >
            ri-briefcase-4-line
          </VIcon>
        </div>
      </VCardText>
    </VCard>

    <!-- ========================= -->
    <!-- A. KPI / STATS -->
    <!-- ========================= -->
    <VRow
      dense
      class="flex-nowrap overflow-x-auto mx-0 pa-0"
      :class="{ 'd-flex': $vuetify.display.xs }"
    >
      <VCol
        cols="4"
        sm="3"
        md="3"
      >
        <StatsCard 
          title="Active Listings"
          short-title="Active"
          value="24"
          icon="ri-list-check"
          chip="+12%"
        />
      </VCol>

      <VCol
        cols="4"
        sm="3"
        md="3"
      >
        <StatsCard 
          title="Pending Applications"
          short-title="Pending"
          value="16"
          icon="ri-time-line"
          chip="8 today"
        />
      </VCol>

      <VCol
        cols="4"
        sm="3"
        md="3"
      >
        <StatsCard 
          title="Confirmed Bookings"
          short-title="Confirmed"
          value="42"
          icon="ri-check-line"
          chip="This week"
        />
      </VCol>

      <VCol
        cols="4"
        sm="3"
        md="3"
      >
        <StatsCard 
          title="Registered Surgeons"
          short-title="Surgeons"
          value="12"
          icon="ri-user-3-line"
          chip="6 active"
        />
      </VCol>
    </VRow>

    <!-- ========================= -->
    <!-- B. QUICK ACTIONS -->
    <!-- ========================= -->
    <div class="quick-actions">
      <VRow
        class="mt-0 mb-0 mt-md-2"
        dense
      >
        <VCol
          cols="12"
          sm="4"
          md="4"
          class="pa-1 d-flex justify-start"
        >
          <VBtn
            class="create-listing-btn pa-2"
            color="primary"
            @click="handleOpenListingModal"
          >
            <div class="create-listing-btn__content">
              <VIcon
                icon="ri-add-circle-line"
                size="30"
                class="create-listing-btn__icon"
              />
              <div class="create-listing-btn__text">
                <div class="text-h6 font-weight-medium">
                  Create New Listing
                </div>
                <div class="text-body-2">
                  Post a new anaesthetist position
                </div>
              </div>
            </div>
          </VBtn>
        </VCol>
      </VRow>
    </div>

    <!-- ========================= -->
    <!-- C. LISTINGS TABLE -->
    <!-- ========================= -->
    <ListingTable 
      :open-in-modal="true"
      @edit-listing="handleEditListing"
    />

    <!-- ========================= -->
    <!-- D. LISTING MODAL (Create/Edit) -->
    <!-- ========================= -->
    <VDialog
      v-model="listingModalOpen"
      max-width="1200"
      width="95vw"
      persistent
      scrollable
    >
      <VCard class="listing-modal-card">
        <VCardTitle class="d-flex justify-space-between align-center sticky-header bg-surface">
          <span>
            {{ listingModalMode === 'edit' ? 'Edit Listing' : 'Create New Listing' }}
          </span>
          <VBtn
            icon="ri-close-line"
            variant="text"
            @click="handleCloseListingModal"
          />
        </VCardTitle>

        <VDivider />

        <VCardText class="px-4 pb-4 pt-0">
          <VProgressLinear
            v-if="listingModalLoading"
            indeterminate
            class="mb-4"
          />

          <VAlert
            v-if="listingModalError"
            type="error"
            class="mb-4"
          >
            {{ listingModalError }}
          </VAlert>

          <ListingForm
            v-if="!listingModalLoading && listingToEdit"
            :initial-data="listingToEdit || {}"
            :is-modal="true"
            :is-edit="Boolean(listingToEdit?.id)"
            context="modal"
            @submit="handleListingFormSubmit"
            @cancel="handleListingFormCancel"
            @modal-close-request="handleListingFormModalCloseRequest"
            @save-draft="handleListingFormSubmit"
          />
        </VCardText>
      </VCard>
    </VDialog>
  </div>
</template>

<script setup>
import ListingTable from '@/components/dashboard/ListingTable.vue'
import ListingForm from '@/components/Listing/ListingForm.vue'
import StatsCard from '@/components/ux/StatsCard.vue'
import { useUserDisplayName } from '@/composables/useUserDisplayName'
import { useListingStore } from '@/stores/listing'
import { $api } from '@/utils/api'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

definePage({
  meta: {
    layout: 'default',
    requiresAuth: true,
    roles: ['surgeon_secretary'],
  },
})

const listingStore = useListingStore()
const { pagination, filters } = storeToRefs(listingStore)
const route = useRoute()
const router = useRouter()

const searchQuery = ref(filters.value.search ?? '')
let searchDebounceHandle
let skipNextSearchUpdate = false

const userName = useUserDisplayName('Surgeon Secretary')

// ← NEW: Stato modale
const listingModalOpen = ref(false)
const listingModalLoading = ref(false)
const listingModalError = ref('')
const listingToEdit = ref(null)
const listingModalMode = computed(() => listingToEdit.value?.id ? 'edit' : 'create')

// ← NEW: Handler apertura modale CREATE
const handleOpenListingModal = async () => {
  listingToEdit.value = null
  listingModalError.value = ''
  listingModalLoading.value = true
  listingModalOpen.value = true

  try {
    const response = await $api('/api/listings/prepare-store', { method: 'GET' })

    listingToEdit.value = response?.data ?? response ?? {}
  } catch (err) {
    console.error('Failed to load listing defaults', err)
    listingModalError.value = 'Unable to load listing defaults.'
  } finally {
    listingModalLoading.value = false
  }
}

// ← NEW: Handler apertura modale EDIT
const handleEditListing = async listing => {
  const listingId = listing?.id
  if (!listingId)
    return

  listingToEdit.value = null
  listingModalError.value = ''
  listingModalLoading.value = true
  listingModalOpen.value = true

  try {
    const response = await $api(`/api/listings/${listingId}/edit`, { method: 'GET' })

    listingToEdit.value = response?.data ?? response ?? listing
  } catch (err) {
    console.error('Failed to load listing details', err)
    listingToEdit.value = listing
    listingModalError.value = 'Unable to load complete listing details. Some state actions may be unavailable.'
  } finally {
    listingModalLoading.value = false
  }
}

// ← NEW: Handler chiusura modale
const handleCloseListingModal = () => {
  listingModalOpen.value = false
  listingModalLoading.value = false
  listingModalError.value = ''
  listingToEdit.value = null
}

// ← NEW: Handler submit form
const handleListingFormSubmit = async payload => {
  try {
    const isEdit = Boolean(listingToEdit.value?.id)

    const url = isEdit 
      ? `/api/listings/${listingToEdit.value.id}`
      : '/api/listings/save-draft'

    const method = isEdit ? 'PUT' : 'POST'
    
    await $api(url, {
      method,
      body: payload,
    })
    
    await listingStore.fetchListings()
    handleCloseListingModal()
  } catch (err) {
    console.error('Failed to save listing', err)
  }
}

// ← NEW: Handler modal-close-request
const handleListingFormModalCloseRequest = async payload => {
  const { refreshParent, autoClose } = payload

  if (refreshParent) {
    await listingStore.fetchListings()
  }

  if (autoClose !== false) {
    handleCloseListingModal()
  }
}

const handleListingFormCancel = () => {
  handleCloseListingModal()
}

const consumeSidebarCreateAction = async () => {
  if (route.query.action !== 'create-listing')
    return

  await handleOpenListingModal()

  const nextQuery = { ...route.query }

  delete nextQuery.action
  router.replace({ query: nextQuery })
}

onMounted(() => {
  consumeSidebarCreateAction()
})

watch(() => route.query.action, value => {
  if (value === 'create-listing')
    consumeSidebarCreateAction()
})

const applySearch = value => {
  listingStore.setFilters({ search: value ?? '' })
  listingStore.setPagination({
    page: 1,
    itemsPerPage: pagination.value.itemsPerPage,
  })
  listingStore.fetchListings()
}

const scheduleSearch = value => {
  if (searchDebounceHandle)
    clearTimeout(searchDebounceHandle)

  searchDebounceHandle = setTimeout(() => {
    applySearch(value)
    searchDebounceHandle = undefined
  }, 400)
}

watch(searchQuery, value => {
  if (skipNextSearchUpdate) {
    skipNextSearchUpdate = false
    
    return
  }
  scheduleSearch(value)
})

watch(() => filters.value.search, value => {
  if (searchDebounceHandle) {
    clearTimeout(searchDebounceHandle)
    searchDebounceHandle = undefined
  }

  if (value === searchQuery.value)
    return

  skipNextSearchUpdate = true
  searchQuery.value = value ?? ''
})

onBeforeUnmount(() => {
  if (searchDebounceHandle)
    clearTimeout(searchDebounceHandle)
  searchDebounceHandle = undefined
})
</script>

<style scoped>
.dashboard-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.quick-actions {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
}

.create-listing-btn {
  block-size: auto;
  inline-size: auto;
  min-block-size: auto;
  text-transform: none;
  color: rgb(var(--v-theme-on-primary));
  background-color: rgb(var(--v-theme-primary));
  background-image: linear-gradient(rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1));
  transition: background-color 0.2s ease;
}

.create-listing-btn.v-btn--density-default {
  height: calc(var(--v-btn-height) + 19px);
}

.create-listing-btn:hover {
  background-color: rgb(var(--v-theme-primary)) !important;
}

.create-listing-btn__content {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  text-align: left;
}

.create-listing-btn__icon {
  display: flex;
  align-items: center;
}

.create-listing-btn__text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
}

.create-listing-btn__text .text-h6 {
  font-size: 1.0625rem;
}

.create-listing-btn__text .text-body-2 {
  font-size: 0.9375rem;
}

.listing-modal-card {
  min-height: 85vh;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
}
</style>
